import { ApiPromiseClient } from "./apiClient/ApiClient";
import * as containmentApi from "./modules/containment";
import * as dowloadApi from "./modules/file";
import * as permissionsApi from "./modules/permission";
import * as plantApi from "./modules/plant";
import * as userApi from "./modules/user";
import { Store } from "store";
import * as dotEnv from "lib/dotEnv";
import {
	BaseRequestConfig,
	SearchPayload,
	SubmitContainmentPayload,
} from "lib/api";
import { DashboardFilters } from "pages";

import { Containment, ContainmentDTO } from "models";
import * as opt from "lib/opt";
import { PlantQuery } from "./modules/plant";
import * as containmentMapper from "models/mapper/containment";
import { MccUser } from "auth";
import {
	AddOrUpdateUserRequest,
	UserFilter,
	DeleteSectorAdminRequest,
	AddSectorAdminRequest,
	DeletePlantUserRequest,
} from "models/dto/user";

let mccApiPromise: Opt<MccApi> = opt.None();
export class MccApi extends ApiPromiseClient {
	private containmentMapper = containmentMapper.New();

	constructor(store: Store) {
		super(dotEnv.Get("BASE_API_URL"), store);
	}

	static GetInstance(store: Store): MccApi {
		if (mccApiPromise.IsNone()) {
			mccApiPromise = opt.Some(new MccApi(store));
		}
		return mccApiPromise.Expect("MccApi instance not found");
	}

	SearchContainments = (
		filters: SearchPayload<Containment, DashboardFilters>
	) => containmentApi.SearchContainments(this, filters);

	SearchUsers = (filters: SearchPayload<MccUser, UserFilter>) =>
		userApi.SearchUsers(this, filters);

	ContainmentById = (id: string) => containmentApi.ContainmentById(this, id);

	PlantStatusByContainment = (id: string, config?: BaseRequestConfig) =>
		plantApi.PlantStatusByContainment(this, id, config);

	UnmappedFiles = (containmentId: string) =>
		containmentApi.UnmappedFiles(this, containmentId);

	ContainmentDataDownload = (
		containmentId: string,
		holdSystemId: string,
		plantId: string
	) =>
		containmentApi.ContainmentDataDownload(
			this,
			containmentId,
			holdSystemId,
			plantId
		);

	ContainmentTypes = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentTypes(this, config);

	ContainmentSectors = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentSectors(this, config);

	SectorsByContainmentType = (typeName: string, config?: BaseRequestConfig) =>
		containmentApi.SectorsByContainmentType(this, typeName, config);

	ContainmentReasons = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentReasons(this, config);

	EvaluateFlexNetPartNumberGate = (
		plants: containmentApi.FlexNetPartNumberGatePlant[]
	) => containmentApi.EvaluateFlexNetPartNumberGate(this, plants);

	AddSerials = (body: File, id: string, byLpn?: boolean, config?: BaseRequestConfig) =>
		containmentApi.AddSerials(this, body, id, byLpn, config);
	UnlockContainment = (body: File, id: string, byLpn: boolean, config?: BaseRequestConfig) =>
		containmentApi.UnlockContainment(this, body, id, byLpn, config);
	ReleaseSerials = (
		body: File,
		id: string,
		plants: any[] = [],
		config?: BaseRequestConfig
	) => containmentApi.ReleaseSerials(this, body, id, plants, config);

	AddContainment = (
		body: SubmitContainmentPayload,
		config?: BaseRequestConfig
	) => containmentApi.AddContainment(this, body, config);

	ContainmentStatuses = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentStatuses(this, config);

	ContainmentInitialHolds = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentInitialHolds(this, config);

	ContainmentSubsequentHolds = (config?: BaseRequestConfig) =>
		containmentApi.ContainmentSubsequentHolds(this, config);

	PlantTypes = (config?: BaseRequestConfig) =>
		plantApi.PlantTypes(this, config);

	Plants = (query: PlantQuery = {}) => plantApi.Plants(this, query);

	UpdateContainment = (id: string, dto: ContainmentDTO) =>
		containmentApi.UpdateContainment(this, this.containmentMapper, id, dto);

	DownloadFile = (filename: string) =>
		dowloadApi.DownloadFile(this, filename);

	DownloadExcelTemplate = (fileName: string) =>
		dowloadApi.DownloadExcelTemplate(this, fileName);

	PartSerialStatuses = <T>(config?: BaseRequestConfig) =>
		containmentApi.PartSerialStatuses<T>(this, config);

	Permissions = () => permissionsApi.Permissions(this);

	AddUser = (request: AddOrUpdateUserRequest) =>
		userApi.AddUser(this, request);

	UpdateUser = (request: AddOrUpdateUserRequest) =>
		userApi.UpdateUser(this, request);

	Roles = () => userApi.Roles(this);

	DeleteUser = (userRoleId: string) => userApi.DeleteUser(this, userRoleId);

	SearchGmid = (id: string) => userApi.SearchGmid(this, id);

	GetUserRoleNotesByBusinessUserId = (businessUserId: string) =>
		userApi.GetUserRoleNotesByBusinessUserId(this, businessUserId);

	UpdateUserRoleNote = (userRoleId: string, noteId: string) =>
		userApi.UpdateUserRoleNote(this, userRoleId, noteId);

	FindSectorAdminContactInfo = () =>
		userApi.FindSectorAdminContactInfo(this);

	FindPlantUserAccessManagersContactInfo = () =>
		userApi.FindPlantUserAccessManagersContactInfo(this);

	CheckUserExists = (businessUserId: string) =>
		userApi.CheckUserExists(this, businessUserId);

	CreatePlantUser = (request: import("models/dto/user").CreatePlantUserRequest) =>
		userApi.CreatePlantUser(this, request);

	GetPlantUser = (businessUserId: string) =>
		userApi.GetPlantUser(this, businessUserId);

	UpdatePlantUser = (businessUserId: string, request: import("models/dto/user").UpdatePlantUserRequest) =>
		userApi.UpdatePlantUser(this, businessUserId, request);
	DeleteSectorAdmin = (
		businessUserId: string,
		request: DeleteSectorAdminRequest
	) => userApi.DeleteSectorAdmin(this, businessUserId, request);

	DeletePlantUser = (
		businessUserId: string,
		request: DeletePlantUserRequest
	) => userApi.DeletePlantUser(this, businessUserId, request);

	AddSectorAdmin = (request: AddSectorAdminRequest) =>
		userApi.AddSectorAdmin(this, request);
}
